import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useRef } from "react";
import type { EntityChangeMessage } from "@repo/services";
import {
  getWorkflowFieldsQueryKey,
  fetchWorkflowLists,
  getWorkflowListsQueryKey,
} from "@repo/shared-components";
import { configurationApi } from "~/api";
import { getTasksQueryKey } from "~/apis/use-tasks";
import { useConfigurationSync, useFileSync } from "~/hooks/use-sync";
import type { Item, Status } from "~/types/api";
import type { ExplorerItem } from "../task.types";
import { fetchStatuses, getStatusesQueryKey } from "./apis/use-statuses";
import { fetchTaskItems, getTaskItemsQueryKey } from "./apis/use-task-items";
import {
  useTabWindowsActions,
  useTabWindowsState,
} from "../tab-windows/hooks/use-tab-windows-context";
import {
  groupItemsByStatus,
  applyListFilters,
  getTaskIdFromWindow,
} from "../tab-windows/utils/window-helpers";

type WorkflowChangeEntity = {
  id?: number;
};

export const useItemSync = () => {
  const queryClient = useQueryClient();
  const { windows, activePanelWindowId } = useTabWindowsState<ExplorerItem>();
  const { addWindow, updateDetailViewForWindow } = useTabWindowsActions<ExplorerItem>();

  const windowsRef = useRef(windows);
  windowsRef.current = windows;

  const activePanelWindowIdRef = useRef(activePanelWindowId);
  activePanelWindowIdRef.current = activePanelWindowId;

  const refreshWindowsForTask = useCallback(
    async (taskId: number) => {
      const items = await queryClient.fetchQuery<Item[]>({
        queryKey: getTaskItemsQueryKey(taskId),
        queryFn: () => fetchTaskItems(taskId),
        staleTime: 0,
      });

      const workflowIds = new Set<number>();
      for (const item of items) {
        if (item.workflowId) workflowIds.add(item.workflowId);
      }

      const statusPromises = Array.from(workflowIds).map((wId) =>
        queryClient.fetchQuery<Status[]>({
          queryKey: getStatusesQueryKey(wId),
          queryFn: () => fetchStatuses(wId),
          staleTime: 10 * 60 * 1000,
        })
      );
      const statuses = (await Promise.all(statusPromises)).flat();

      const currentWindows = windowsRef.current;
      for (const window of currentWindows) {
        const windowTaskIds = window.state.taskIds ?? [];
        const windowTaskId = getTaskIdFromWindow(window.id);

        const isAffected = windowTaskIds.includes(taskId) || windowTaskId === taskId;

        if (!isAffected) continue;

        let allItems = items;
        if (windowTaskIds.length > 1) {
          const otherTaskIds = windowTaskIds.filter((id) => id !== taskId);
          const otherItemsPromises = otherTaskIds.map((tid) =>
            queryClient.fetchQuery<Item[]>({
              queryKey: getTaskItemsQueryKey(tid),
              queryFn: () => fetchTaskItems(tid),
              staleTime: 5 * 60 * 1000,
            })
          );
          const otherItems = (await Promise.all(otherItemsPromises)).flat();
          allItems = [...items, ...otherItems];
        }

        let filteredItems = allItems;
        if (window.state.appliedListIds && window.state.appliedListIds.length > 0) {
          const listWorkflowIds = Array.from(workflowIds);
          const lists = (
            await Promise.all(
              listWorkflowIds.map((wId) =>
                queryClient.fetchQuery({
                  queryKey: getWorkflowListsQueryKey(configurationApi, wId),
                  queryFn: () => fetchWorkflowLists(configurationApi, wId),
                  staleTime: 10 * 60 * 1000,
                })
              )
            )
          ).flat();
          filteredItems = applyListFilters(allItems, lists, window.state.appliedListIds);
        }

        const groupedItems = groupItemsByStatus(filteredItems, statuses);

        addWindow(groupedItems as ExplorerItem[], window.id, window.state.title, {
          taskIds: window.state.taskIds,
        });
      }
    },
    [queryClient, addWindow]
  );

  const handleItemChanged = useCallback(
    (data: EntityChangeMessage) => {
      const entity = data.entity as { taskId?: number } | undefined;
      const taskId = entity?.taskId;

      if (taskId) {
        void refreshWindowsForTask(taskId).catch((error) => {
          console.error("Failed to refresh windows for task", taskId, error);
        });
      }
    },
    [refreshWindowsForTask]
  );

  const handleWorkflowChanged = useCallback(
    (data: EntityChangeMessage) => {
      const entity = data.entity as WorkflowChangeEntity | undefined;
      const newWorkflowId = entity?.id;

      if (newWorkflowId) {
        void queryClient.invalidateQueries({
          queryKey: getWorkflowFieldsQueryKey(configurationApi, newWorkflowId),
        });
        void queryClient.invalidateQueries({ queryKey: getStatusesQueryKey(newWorkflowId) });
        void queryClient.invalidateQueries({ queryKey: getTasksQueryKey() });
      }

      const currentWindows = windowsRef.current;
      const allTaskIds = new Set<number>();
      for (const window of currentWindows) {
        const windowTaskIds = window.state.taskIds ?? [];
        for (const id of windowTaskIds) allTaskIds.add(id);
        const windowTaskId = getTaskIdFromWindow(window.id);
        if (windowTaskId) allTaskIds.add(windowTaskId);
      }

      const openPanelId = activePanelWindowIdRef.current;

      void (async () => {
        await Promise.all(
          Array.from(allTaskIds).map((taskId) =>
            refreshWindowsForTask(taskId).catch((error) => {
              console.error(
                "[useItemSync] Failed to refresh windows after workflow change",
                taskId,
                error
              );
            })
          )
        );
        if (openPanelId) {
          await updateDetailViewForWindow(openPanelId, undefined, true).catch((error) => {
            console.error(
              "[useItemSync] Failed to refresh detail view after workflow change",
              openPanelId,
              error
            );
          });
        }
      })();
    },
    [queryClient, refreshWindowsForTask, updateDetailViewForWindow]
  );

  useFileSync("Item", handleItemChanged);
  useConfigurationSync("Workflow", handleWorkflowChanged);
};
